/*global ApeConfig*/
import { useEffect, useRef, useState } from 'react';
import { useWeb3ModalAccount } from '@web3modal/ethers/react';

const CHAIN_COLORS = {
	8453:  { color: '#0052ff', dark: '#3b7bff', id: 'base' },
	1:     { color: '#627eea', dark: '#8ea0f0', id: 'eth' },
	11155111: { color: '#627eea', dark: '#8ea0f0', id: 'eth' },
	56:    { color: '#f3ba2f', dark: '#f3ba2f', id: 'bnb' },
	97:    { color: '#f3ba2f', dark: '#f3ba2f', id: 'bnb' },
	4663:  { color: '#d6fe52', dark: '#f4c518', id: 'robinhood' },
	137:   { color: '#8247e5', dark: '#a370f7', id: 'polygon' },
	42161: { color: '#28a0f0', dark: '#5bb8f5', id: 'arbitrum' },
	10:    { color: '#ff0420', dark: '#ff3d57', id: 'optimism' },
	43114: { color: '#e84142', dark: '#ef6b6c', id: 'avalanche' },
};

const DEFAULT_ACCENT = { color: '#d6fe52', dark: '#f4c518', id: 'robinhood' };

function hexToRgb(hex) {
	const h = hex.replace('#', '');
	return {
		r: parseInt(h.slice(0, 2), 16),
		g: parseInt(h.slice(2, 4), 16),
		b: parseInt(h.slice(4, 6), 16),
	};
}

function rgba(hex, alpha) {
	const { r, g, b } = hexToRgb(hex);
	return `rgba(${r},${g},${b},${alpha})`;
}

function mixColor(base, accent, weight) {
	const b = hexToRgb(base);
	const a = hexToRgb(accent);
	const m = (x, y) => Math.round(x * (1 - weight) + y * weight).toString(16).padStart(2, '0');
	return '#' + m(b.r, a.r) + m(b.g, a.g) + m(b.b, a.b);
}

function buildAccentCSS(hex, chainId, isDark) {
	const { r, g, b } = hexToRgb(hex);
	const rg = (a) => `rgba(${r},${g},${b},${a})`;
	const mix = (base, w) => mixColor(base, hex, w);

	const chain = CHAIN_COLORS[chainId] || DEFAULT_ACCENT;
	const isDefault = chain.id === 'robinhood';

	let darkVars = '';
	if (isDark && !isDefault) {
		darkVars = `html.pf-dark{--bg:${mix('#0a0a0c', .05)} !important;--card:${mix('#141416', .06)} !important;--card2:${mix('#1b1b1e', .06)} !important;--field:${mix('#212125', .07)} !important;--ink:#f3f3f5 !important;--ink2:#ffffff !important;--bd:${mix('#3a3a42', .12)} !important;--mut:#bab2a8 !important;--mut2:#8f869a !important;--mut3:#756e80 !important;--mut4:#5c5768 !important;--line:${mix('#26262b', .08)} !important;--chartbg:${mix('#0a0a0c', .05)} !important;--chartframe:${mix('#2a2a30', .1)} !important;--bdh:${hex} !important;}`
			+ `html.pf-dark .ant-layout-sider{background-color:${mix('#060608', .05)} !important;}`;
	} else if (isDark && isDefault) {
		darkVars = `html.pf-dark{--bg:#14120b !important;--card:#1e1b12 !important;--card2:#252117 !important;--field:#2a251a !important;--ink:#f4efe1 !important;--ink2:#ffffff !important;--bd:#5c5238 !important;--mut:#bab29d !important;--mut2:#8f8672 !important;--mut3:#75705e !important;--mut4:#5c5748 !important;--line:#2d2817 !important;--chartbg:#0d0b06 !important;--chartframe:#2e2817 !important;--bdh:#b89440 !important;}`
			+ `html.pf-dark .ant-layout-sider{background-color:#0d0b06 !important;}`;
	}

	return darkVars
		+ `:root{--accent:${hex} !important;--accent-soft:${rg(.5)} !important;--accent-wash:${rg(.15)} !important;}`
		+ `html.pf-dark{--accent:${hex} !important;--accent-soft:${rg(.5)} !important;--accent-wash:${rg(.15)} !important;}`
		+ `.pf-card.cf-up{animation:cfUpX .7s ease-out, shakeC .5s ease-in-out !important;}.pf-card.cf-dn{animation:cfDnX .7s ease-out, shakeC .5s ease-in-out !important;}.cf-up{animation:cfUpX .7s ease-out !important;}.cf-dn{animation:cfDnX .7s ease-out !important;}`
		+ `.pf-dark .pf-card.cf-up{animation:cfUpDarkX .7s ease-out, shakeC .5s ease-in-out !important;}.pf-dark .pf-card.cf-dn{animation:cfDnDarkX .7s ease-out, shakeC .5s ease-in-out !important;}.pf-dark .cf-up{animation:cfUpDarkX .7s ease-out !important;}.pf-dark .cf-dn{animation:cfDnDarkX .7s ease-out !important;}`
		+ `@keyframes cfUpX{0%{border-color:${hex};box-shadow:0 0 0 3px ${rg(.85)},0 0 14px 2px ${rg(.6)},3px 4px 0 rgba(38,37,31,.16);}100%{border-color:var(--bd);box-shadow:0 0 0 0 ${rg(0)},0 0 0 0 ${rg(0)},3px 4px 0 rgba(38,37,31,.16);}}`
		+ `@keyframes cfDnX{0%{border-color:${hex};box-shadow:0 0 0 3px ${rg(.85)},0 0 14px 2px ${rg(.6)},3px 4px 0 rgba(38,37,31,.16);}100%{border-color:var(--bd);box-shadow:0 0 0 0 ${rg(0)},0 0 0 0 ${rg(0)},3px 4px 0 rgba(38,37,31,.16);}}`
		+ `@keyframes cfUpDarkX{0%{border-color:${hex};box-shadow:0 0 0 3px ${rg(.7)},0 0 14px 2px ${rg(.5)},3px 4px 0 rgba(0,0,0,.3);}100%{border-color:var(--bd);box-shadow:0 0 0 0 ${rg(0)},0 0 0 0 ${rg(0)},3px 4px 0 rgba(0,0,0,.3);}}`
		+ `@keyframes cfDnDarkX{0%{border-color:${hex};box-shadow:0 0 0 3px ${rg(.7)},0 0 14px 2px ${rg(.5)},3px 4px 0 rgba(0,0,0,.3);}100%{border-color:var(--bd);box-shadow:0 0 0 0 ${rg(0)},0 0 0 0 ${rg(0)},3px 4px 0 rgba(0,0,0,.3);}}`;
}

export function useChainAccent() {
	const { chainId: walletChainId } = useWeb3ModalAccount();
	const prevKeyRef = useRef('');
	const [dark, setDark] = useState(() => document.documentElement.classList.contains('pf-dark'));
	const [appChainId, setAppChainId] = useState(() => {
		const saved = localStorage.getItem('pf-app-chain');
		return saved ? parseInt(saved) : walletChainId || 8453;
	});

	useEffect(() => {
		const observer = new MutationObserver(() => {
			setDark(document.documentElement.classList.contains('pf-dark'));
		});
		observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const handler = (e) => {
			const id = parseInt(e.detail);
			if (id && !isNaN(id)) {
				setAppChainId(id);
				localStorage.setItem('pf-app-chain', id);
			}
		};
		window.addEventListener('pf-chain-change', handler);
		return () => window.removeEventListener('pf-chain-change', handler);
	}, []);

	useEffect(() => {
		if (walletChainId) {
			setAppChainId(walletChainId);
			localStorage.setItem('pf-app-chain', walletChainId);
		}
	}, [walletChainId]);

	const activeChainId = appChainId || walletChainId || 8453;

	useEffect(() => {
		const chain = CHAIN_COLORS[activeChainId] || DEFAULT_ACCENT;
		const hex = dark ? chain.dark : chain.color;
		const key = `${hex}|${activeChainId}|${dark ? 'd' : 'l'}`;

		if (prevKeyRef.current === key) return;
		prevKeyRef.current = key;

		let styleEl = document.getElementById('pf-accent-css');
		if (!styleEl) {
			styleEl = document.createElement('style');
			styleEl.id = 'pf-accent-css';
			document.head.appendChild(styleEl);
		}
		styleEl.textContent = buildAccentCSS(hex, activeChainId, dark);

		const root = document.documentElement;
		if (chain.id === 'base') {
			root.classList.add('pf-chain-base');
		} else {
			root.classList.remove('pf-chain-base');
		}
	}, [activeChainId, dark]);
}

export function emitChainChange(chainId) {
	window.dispatchEvent(new CustomEvent('pf-chain-change', { detail: chainId }));
}
